<template>
  <div class="documents-view">
    <SectionCard title="Dokument">
      <p v-if="isDocumentsLoading" class="text-subtle">Hämtar dokument...</p>
      <p v-else-if="documentsError" class="error-text">{{ documentsError }}</p>
      <template v-else>
        <AppTable v-if="documents.length">
          <thead>
            <tr>
              <th>Dokumenttyp</th>
              <th>Filtyp</th>
              <th>Skapat</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow
              v-for="document in documents"
              :key="document.id"
              class="document-row"
              @click="openDocument(document)"
            >
              <AppTableCell>{{ translateDocumentType(document.type) }}</AppTableCell>
              <AppTableCell>{{ document.fileType.ext.toUpperCase() }}</AppTableCell>
              <AppTableCell nowrap>{{ formatDateTimeValue(document.createdAt) }}</AppTableCell>
              <AppTableCell action>
                <span @click.stop>
                  <AppButton
                    icon
                    small
                    :loading="openingDocumentId === document.id"
                    :aria-label="`Visa ${translateDocumentType(document.type)}`"
                    title="Visa"
                    @click="openDocument(document)"
                  >
                    <SvgIcon :path="mdiMagnify" size="18" />
                  </AppButton>
                </span>
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Inga dokument.</p>
      </template>
    </SectionCard>

    <SectionCard title="Ladda upp dokument">
      <div class="stack">
        <AppSelect
          v-model="selectedType"
          label="Dokumenttyp"
          :options="documentTypeOptions"
          placeholder="Välj dokumenttyp"
        />

        <AppDocumentUpload v-model="selectedFiles" :disabled="isUploading" />

        <div class="actions">
          <p class="text-subtle" aria-live="polite">
            <template v-if="uploadSummary">
              Dokumenttypen <span class="summary-type">{{ uploadSummary.typeLabel }}</span> gäller
              {{ uploadSummary.files }}.
            </template>
          </p>
          <AppButton color="green" :disabled="!canUpload" :loading="isUploading" @click="handleUpload">
            Ladda upp
          </AppButton>
        </div>
      </div>
    </SectionCard>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiMagnify } from '@mdi/js'
import { formatDateTimeValue } from '@staff/shared'
import {
  AppButton,
  AppDocumentUpload,
  AppSelect,
  AppTable,
  AppTableCell,
  AppTableRow,
  notify,
  SectionCard,
  SvgIcon,
} from '@staff/ui'

import { useMetadata } from '@/composables/useMetadata'
import { useDistributorDocuments } from '@/composables/useDistributorDocuments'
import { useDocumentPreview } from '@/composables/documentPreview'
import { uploadUserDocument, type UserDetails } from '@/services/user'

const props = defineProps<{ user: UserDetails }>()

const { translateDocumentType, getReferenceValues } = useMetadata()
const { openingDocumentId, openDocument } = useDocumentPreview((document) => translateDocumentType(document.type))
const {
  documents,
  isLoading: isDocumentsLoading,
  error: documentsError,
  getDocuments,
} = useDistributorDocuments(() => ({ userId: props.user.id }))

const selectedType = ref('')
const selectedFiles = ref<File[]>([])
const isUploading = ref(false)

const documentTypeOptions = computed(() =>
  getReferenceValues('userDocumentUploadTypes').map((ref) => ({
    value: ref.code,
    text: ref.label,
  })),
)

const uploadSummary = computed(() => {
  if (selectedFiles.value.length < 2) return null

  const typeLabel = documentTypeOptions.value.find((option) => option.value === selectedType.value)?.text
  if (!typeLabel) return null

  const files = selectedFiles.value.length === 2 ? 'båda filerna' : `alla ${selectedFiles.value.length} filer`
  return { typeLabel, files }
})

const canUpload = computed(() => selectedFiles.value.length > 0 && selectedType.value !== '' && !isUploading.value)

const handleUpload = async () => {
  if (!canUpload.value) return

  isUploading.value = true
  const files = [...selectedFiles.value]
  let currentFile: File | undefined

  try {
    for (const file of files) {
      currentFile = file
      await uploadUserDocument(props.user.id, { file, type: selectedType.value })
      selectedFiles.value = selectedFiles.value.filter((selected) => selected !== file)
    }

    notify({
      type: 'success',
      title: files.length > 1 ? 'Dokumenten laddades upp' : 'Dokumentet laddades upp',
    })
    selectedType.value = ''
  } catch (err) {
    notify({
      type: 'error',
      title: `Kunde inte ladda upp ${currentFile?.name ?? 'dokument'}`,
      description: err instanceof Error ? err.message : undefined,
      dismissable: true,
    })
  } finally {
    isUploading.value = false
    await getDocuments()
  }
}
</script>

<style scoped lang="scss">
.documents-view {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-12);
}

.summary-type {
  font-weight: var(--font-weight-semibold);
}

.document-row {
  cursor: pointer;
}
</style>
